import { API_KEY_MAX_EXPIRY_DAYS } from '@atlas/types'
import { z } from 'zod'

// ─── Public-facing schema (create / update inputs) ────────────────────────────

/**
 * `POST /users/me/api-keys`.
 *
 * `.strict()` so a caller cannot smuggle server-owned fields — `userId`,
 * `keyHash`, `revokedAt`, `expiresAt` as an absolute date — into a create. The
 * owner comes from the authenticated session, never from the body.
 *
 * Lifetime is expressed in days rather than as a date so the maximum is checkable
 * here: an absolute timestamp would have to be compared against "now" inside the
 * service, putting the bound somewhere a reader of the schema cannot see it.
 * Over the maximum is REJECTED, not clamped — silently shortening a requested
 * year to 90 days would leave the caller believing a key outlives its real end.
 */
export const apiKeyCreateSchema = z
	.object({
		name: z.string().trim().min(1).max(100),
		expiresInDays: z.number().int().positive().max(API_KEY_MAX_EXPIRY_DAYS).optional(),
	})
	.strict()

/**
 * `PATCH /users/me/api-keys/{id}`. The label, and nothing else.
 *
 * There is deliberately no update for the secret, the expiry or any authority: a
 * key's power is its owner's, and rotation is create-new then revoke-old rather
 * than mutation in place. Spelled out as its own object rather than
 * `apiKeyCreateSchema.partial()`, which would have quietly accepted
 * `expiresInDays` and let a caller believe a lifetime had been extended.
 */
export const apiKeyUpdateSchema = z
	.object({
		name: z.string().trim().min(1).max(100),
	})
	.strict()

export type ApiKeyCreateInput = z.infer<typeof apiKeyCreateSchema>
export type ApiKeyUpdateInput = z.infer<typeof apiKeyUpdateSchema>
